import {
  annotationContentBox,
  annotationSketchStrokes,
} from "@/lib/annotation-sketch";

export type AgentAnnotationShape =
  | "rectangle"
  | "rounded_rectangle"
  | "square"
  | "circle"
  | "ellipse"
  | "marker"
  | "underline"
  | "path"
  | "polyline";

export type AgentAnnotationEmphasis = "subtle" | "normal" | "strong";
export type AgentAnnotationTransition = "instant" | "linear" | "ease_in_out";

interface AgentAnnotationBase {
  /** Stable identity for progressive updates within one presentation. */
  annotationId?: string;
  /** Execution that owns this mark and its retained explanation. */
  executionId?: string;
  /** User dismissal survives progressive updates and desktop mirror hydration. */
  dismissed?: boolean;
  /** Wall-clock ms when this mark first appeared; progressive updates keep it. */
  drawnAt?: number;
  displayId: number;
  globalX: number;
  globalY: number;
  width: number;
  height: number;
  seq: number;
  emphasis?: AgentAnnotationEmphasis;
  strokeWidth?: number;
  fillOpacity?: number;
  padding?: number;
  transition?: AgentAnnotationTransition;
  transitionDurationMs?: number;
  /** Trace path geometry from start to finish instead of only fading it in. */
  animateTrace?: boolean;
  /** Presentation-only pen progress (0–1) while a highlight is being drawn;
   * absent means fully drawn. Not part of the agent contract. */
  drawProgress?: number;
  /** Explanation retained with this mark after the live cursor moves on. It
   * is handwritten beside the mark (handwrittenCalloutPlacement). */
  callout?: {
    content: string;
    /** Cursor-ownership anchor only: where the presenting cursor rests while
     * it narrates this callout (set by canvas-cursor-presentation). */
    globalX?: number;
    globalY?: number;
    role?: "context" | "answer";
    /** Set only after this presentation completes successfully. */
    delivered?: boolean;
  };
}

export type AgentAnnotationTarget =
  | (AgentAnnotationBase & {
      shape:
        | "rectangle"
        | "rounded_rectangle"
        | "square"
        | "circle"
        | "ellipse"
        | "marker"
        | "underline";
    })
  | (AgentAnnotationBase & {
      shape: "path" | "polyline";
      /** Target-local normalized points, measured from the target's top-left. */
      path: Array<{ x: number; y: number }>;
      arrowEnd?: boolean;
    });

/** Matches the maximum desktop_interact sequence length. The bound applies to
 * one presentation delivery so earlier calls in the same execution remain. */
export const MAX_PRESENTATION_ANNOTATIONS = 100;

type AnnotationCursor = {
  annotationTarget?: AgentAnnotationTarget;
  annotationTargets?: AgentAnnotationTarget[];
  pointTarget?: { seq: number; globalX: number; globalY: number };
};

type DisplayCursor = {
  pointTarget?: { displayId: number };
};

/** Returns retained marks while preserving legacy single-target consumers. */
export function agentCursorAnnotationTargets(
  cursor: AnnotationCursor | null | undefined,
): AgentAnnotationTarget[] {
  if (!cursor) return [];
  return cursor.annotationTargets?.length
    ? cursor.annotationTargets
    : cursor.annotationTarget
      ? [cursor.annotationTarget]
      : [];
}

/** Replaces one progressive mark and bounds presentation-owned history. */
export function upsertPresentationAnnotation(
  targets: readonly AgentAnnotationTarget[],
  incoming: AgentAnnotationTarget,
): AgentAnnotationTarget[] {
  const annotationId = incoming.annotationId;
  if (
    annotationId &&
    targets.some(
      (target) => target.annotationId === annotationId && target.dismissed,
    )
  )
    incoming = { ...incoming, dismissed: true };
  if (!annotationId) {
    return [...targets, incoming].slice(-MAX_PRESENTATION_ANNOTATIONS);
  }

  const deliverySeparator = annotationId.indexOf(":");
  if (deliverySeparator < 0) {
    const next = targets.filter(
      (target) => target.annotationId !== annotationId,
    );
    next.push(incoming);
    return next.slice(-MAX_PRESENTATION_ANNOTATIONS);
  }

  const deliveryPrefix = annotationId.slice(0, deliverySeparator + 1);
  const retainedFromOtherDeliveries: AgentAnnotationTarget[] = [];
  const currentDelivery: AgentAnnotationTarget[] = [];
  for (const target of targets) {
    if (target.annotationId === annotationId) continue;
    if (target.annotationId?.startsWith(deliveryPrefix)) {
      currentDelivery.push(target);
    } else {
      retainedFromOtherDeliveries.push(target);
    }
  }
  currentDelivery.push(incoming);
  return [
    ...retainedFromOtherDeliveries,
    ...currentDelivery.slice(-MAX_PRESENTATION_ANNOTATIONS),
  ];
}

export interface AnnotationPointerTarget {
  shape: AgentAnnotationShape;
  globalX: number;
  globalY: number;
  width: number;
  height: number;
  padding?: number;
  path?: ReadonlyArray<{ x: number; y: number }>;
  /** Seed identity, so the pen starts where the sketch ink starts. */
  annotationId?: string;
  seq?: number;
}

/** Stable global point where the agent pen touches a mark: the start of the
 * sketch stroke for closed shapes, the endpoint for paths. */
export function annotationPointerAnchor(target: AnnotationPointerTarget): {
  x: number;
  y: number;
} {
  if (target.shape === "path" || target.shape === "polyline") {
    const endpoint = target.path?.[target.path.length - 1];
    if (endpoint) {
      return {
        x: target.globalX + endpoint.x * target.width,
        y: target.globalY + endpoint.y * target.height,
      };
    }
    const box = annotationContentBox(target);
    return { x: box.left, y: box.top };
  }
  const { box, strokes } = annotationSketchStrokes(target);
  const start = strokes[0]?.points[0] ?? { x: 0, y: 0 };
  return { x: box.left + start.x, y: box.top + start.y };
}

/** True while the live cursor is already rendering this retained callout. */
export function cursorOwnsAnnotationCallout(
  cursor: AnnotationCursor | null | undefined,
  target: AgentAnnotationTarget,
): boolean {
  return !!(
    target.callout?.content.trim() &&
    target.callout.globalX !== undefined &&
    target.callout.globalY !== undefined &&
    cursor?.pointTarget &&
    cursor.pointTarget.seq === target.seq &&
    cursor.pointTarget.globalX === target.callout.globalX &&
    cursor.pointTarget.globalY === target.callout.globalY
  );
}

/** Only a live point owns cursor routing; retained marks render independently. */
export function cursorTargetIsOnDisplay(
  cursor: DisplayCursor,
  displayId: number | null,
): boolean {
  if (displayId === null) return true;
  if (cursor.pointTarget) return cursor.pointTarget.displayId === displayId;
  return true;
}

export interface ClientRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface AnnotationGeometry extends ClientRect {
  width: number;
  height: number;
  /** Unclipped shape bounds, expressed inside the clipped SVG viewport. */
  contentLeft: number;
  contentTop: number;
  contentWidth: number;
  contentHeight: number;
}

/** Converts a global annotation into display-local, viewport-clipped
 * geometry. `overflow` inflates the box around the content box (for ink that
 * reaches past it) before clipping. */
export function annotationGeometry(
  target: AgentAnnotationTarget,
  origin: { x: number; y: number },
  viewport?: { width: number; height: number; inset?: number },
  overflow = 0,
): AnnotationGeometry | null {
  const box = annotationContentBox(target);
  const contentLeft = box.left - origin.x;
  const contentTop = box.top - origin.y;
  const sourceLeft = contentLeft - overflow;
  const sourceTop = contentTop - overflow;
  const sourceRight = contentLeft + box.width + overflow;
  const sourceBottom = contentTop + box.height + overflow;
  const inset = Math.max(0, viewport?.inset ?? 4);
  const left = viewport ? Math.max(sourceLeft, inset) : sourceLeft;
  const top = viewport ? Math.max(sourceTop, inset) : sourceTop;
  const right = viewport
    ? Math.min(sourceRight, viewport.width - inset)
    : sourceRight;
  const bottom = viewport
    ? Math.min(sourceBottom, viewport.height - inset)
    : sourceBottom;
  if (right <= left || bottom <= top) return null;

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    contentLeft: contentLeft - left,
    contentTop: contentTop - top,
    contentWidth: box.width,
    contentHeight: box.height,
  };
}
